import { useState, useEffect, useCallback } from 'react';
import type { FC, ReactElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Tooltip,
  useMediaQuery,
  useTheme,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  GetApp as DownloadIcon,
  PictureAsPdf as PdfIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { User, UserFilters } from '../../types/user';
import { usersApi } from '../../services/api';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';

// Function to export data to Excel file
const exportToExcel = (data: any[], filename: string) => {
  // Create a new workbook
  const wb = XLSX.utils.book_new();
  
  // Convert data to worksheet
  const ws = XLSX.utils.json_to_sheet(data);
  
  // Add worksheet to workbook
  XLSX.utils.book_append_sheet(wb, ws, "Users");
  
  // Generate Excel binary
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  
  // Create a Blob and trigger download
  const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  saveAs(blob, filename);
};

const UserList: FC = (): ReactElement => {
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [users, setUsers] = useState<User[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState<UserFilters>({
    page: 1,
    limit: 10,
  });
  
  // Add theme and responsive breakpoints
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // State to track API errors
  const [error, setError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null); // Reset error state before each fetch
      
      const response = await usersApi.getUsers(filters);
      
      // Validate the response format
      if (!response || !response.users || !Array.isArray(response.users)) {
        throw new Error('Invalid response format from API');
      }
      
      // Only set the users if we receive a valid array
      setUsers(response.users);
      setTotal(response.pagination.total || 0);
      
      // Log successful data fetch for debugging
      console.log(`Successfully fetched ${response.users.length} users`);
    } catch (error) {
      console.error('Error fetching users:', error);
      setError('حدث خطأ أثناء جلب بيانات المستخدمين. يرجى التحقق من اتصال قاعدة البيانات والمحاولة مرة أخرى.');
      setUsers([]); // Clear any potentially fake/invalid data
      setTotal(0);
    } finally {
      setIsLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleDelete = async (id: string) => {
    if (window.confirm('هل أنت متأكد من حذف هذا المستخدم؟')) {
      try {
        await usersApi.deleteUser(id);
        fetchUsers();
      } catch (error) {
        console.error('Error deleting user:', error);
      }
    }
  };

  const handleFilterChange = (key: keyof UserFilters, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (event: unknown, newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage + 1 }));
  };

  const handleExcelExport = () => {
    // Prepare data for Excel export
    const excelData = users.map(user => {
      return {
        'اسم المستخدم': user.username,
        'الاسم الكامل': user.fullName,
        'البريد الإلكتروني': user.email,
        'الهاتف': user.phone || '',
        'الحالة': user.status === 'active' ? 'نشط' : 'غير نشط',
        'آخر تسجيل دخول': user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('ar-SA') : '-'
      };
    });

    const timestamp = new Date().toISOString().slice(0,10);
    exportToExcel(excelData, `users_${timestamp}.xlsx`);
  };

  const handlePdfExport = () => {
    try {
      // Create a professional PDF with A4 landscape orientation
      const doc = new jsPDF('l', 'mm', 'a4');
      
      // Set RTL mode for Arabic support
      doc.setR2L(true);
      
      // Add document title
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.setTextColor(44, 62, 80);
      
      const title = "قائمة المستخدمين";
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.text(title, pageWidth/2, 15, { align: "center" });
      
      // Add date
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      const date = new Date().toLocaleDateString('ar-SA', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      doc.text(`تاريخ التقرير: ${date}`, pageWidth - 15, 15, { align: "right" });
      
      // Add summary info
      doc.setFontSize(12);
      doc.setTextColor(52, 73, 94);
      doc.text(`إجمالي المستخدمين: ${total}`, 15, 15);
      
      // Draw separator line
      doc.setDrawColor(41, 128, 185);
      doc.setLineWidth(0.5);
      doc.line(10, 20, pageWidth - 10, 20);
      
      // Refined headers with Arabic text
      const headers = [
        { header: 'اسم المستخدم', key: 'username' },
        { header: 'الاسم الكامل', key: 'fullName' },
        { header: 'البريد الإلكتروني', key: 'email' },
        { header: 'الهاتف', key: 'phone' },
        { header: 'الحالة', key: 'status' },
        { header: 'آخر تسجيل دخول', key: 'lastLogin' }
      ];
      
      // Enhanced data transformation with Arabic labels
      const enhancedData = users.map(user => {
        return {
          username: user.username,
          fullName: user.fullName,
          email: user.email || '-',
          phone: user.phone || '-',
          status: user.status === 'active' ? 'نشط' : 'غير نشط',
          lastLogin: user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('ar-SA') : '-'
        };
      });
      
      // Setup table columns and data
      const tableColumns = headers.map(h => h.header);
      const tableData = enhancedData.map(row =>
        headers.map(h => row[h.key as keyof typeof row])
      );
      
      // Apply professional table styling
      autoTable(doc, {
        head: [tableColumns],
        body: tableData,
        startY: 25,
        theme: 'grid',
        styles: {
          fontSize: 10,
          cellPadding: 3,
          lineColor: [220, 220, 220],
          lineWidth: 0.1,
          halign: 'right', // Right align for Arabic
          textColor: [50, 50, 50],
          font: 'helvetica'
        },
        headStyles: {
          fillColor: [41, 128, 185],
          textColor: 255,
          fontStyle: 'bold',
          halign: 'center',
          fontSize: 11
        },
        alternateRowStyles: {
          fillColor: [245, 245, 245]
        },
        margin: { top: 25, right: 10, bottom: 10, left: 10 },
        didDrawPage: (data) => {
          // Add footer on each page
          doc.setFontSize(8);
          doc.setTextColor(150, 150, 150);
          const footerText = "© 2025 نظام إدارة المستخدمين - Aliphia Systems";
          doc.text(footerText, pageWidth/2, pageHeight - 10, { align: "center" });
          
          // Add page numbers
          doc.text(`الصفحة ${doc.getNumberOfPages()}`, pageWidth - 15, pageHeight - 10, { align: "right" });
        }
      });
      
      // Save with a timestamp and better filename
      const timestamp = new Date().toISOString().slice(0,10);
      doc.save(`تقرير_المستخدمين_${timestamp}.pdf`);
      
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(`حدث خطأ أثناء تصدير ملف PDF: ${error instanceof Error ? error.message : 'خطأ غير معروف'}`);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(to bottom, #f8fafc, #eef2f6)' }}>
      {/* Header Section with improved gradient */}
      <Box
        sx={{
          p: 3,
          bgcolor: 'white',
          borderRadius: '0 0 16px 16px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          overflow: 'hidden',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            height: '4px',
            background: 'linear-gradient(to right, #9c27b0, #673ab7)'
          }
        }}
      >
        {/* Title and Button Row */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4,
        }}>
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 600,
                mb: 1,
                color: '#2c3e50',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: -8,
                  right: 0,
                  width: 50,
                  height: 3,
                  bgcolor: 'secondary.main',
                  borderRadius: 1
                }
              }}
            >
              إدارة المستخدمين
            </Typography>
            
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
                mt: 2,
                mb: 2,
                maxWidth: 500
              }}
            >
              يمكنك عرض وإدارة حسابات المستخدمين، وتصدير القوائم بتنسيقات مختلفة
            </Typography>
          </Box>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/users/new')}
            disabled={!hasPermission('admin', 'users', 'create')}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.2,
              bgcolor: 'secondary.main',
              boxShadow: '0 4px 12px rgba(156, 39, 176, 0.2)',
              transition: 'transform 0.2s, box-shadow 0.2s',
              '&:hover': {
                bgcolor: 'secondary.dark',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(156, 39, 176, 0.3)'
              }
            }}
          >
            إضافة مستخدم جديد
          </Button>
        </Box>

        {/* Export Buttons */}
        <Box sx={{
          display: 'flex',
          gap: 2,
          mb: 4,
          flexWrap: 'wrap'
        }}>
          <Tooltip title="تصدير اكسل" arrow>
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleExcelExport}
              sx={{
                color: 'success.main',
                borderColor: 'success.main',
                borderRadius: 2,
                px: 3,
                fontWeight: 500,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(76, 175, 80, 0.08)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(76, 175, 80, 0.15)'
                }
              }}
              variant="outlined"
              size="medium"
            >
              تصدير اكسل
            </Button>
          </Tooltip>
          
          <Tooltip title="تصدير PDF" arrow>
            <Button
              startIcon={<PdfIcon />}
              onClick={handlePdfExport}
              sx={{
                color: 'primary.main',
                borderColor: 'primary.main',
                borderRadius: 2,
                px: 3,
                fontWeight: 500,
                transition: 'all 0.2s',
                '&:hover': {
                  bgcolor: 'rgba(33, 150, 243, 0.08)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(33, 150, 243, 0.15)'
                }
              }}
              variant="outlined"
              size="medium"
            >
              تصدير PDF
            </Button>
          </Tooltip>
        </Box>

        {/* Advanced Filters Section */}
        <Card
          elevation={0}
          sx={{
            bgcolor: 'white',
            borderRadius: 4,
            border: '1px solid rgba(0,0,0,0.06)',
            boxShadow: '0 10px 25px rgba(0,0,0,0.05)',
            overflow: 'visible',
            position: 'relative',
            mt: 2,
            mb: 2,
            transform: 'translateY(20px)',
            transition: 'all 0.3s ease',
            '&:hover': {
              boxShadow: '0 15px 35px rgba(0,0,0,0.1)',
              transform: 'translateY(15px) scale(1.01)'
            }
          }}
        >
          {/* Filter Badge */}
          <Box
            sx={{
              position: 'absolute',
              top: -18,
              right: 24,
              bgcolor: 'secondary.main',
              background: 'linear-gradient(90deg, #9c27b0, #673ab7)',
              color: 'white',
              py: 0.8,
              px: 3,
              borderRadius: '30px',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              boxShadow: '0 5px 15px rgba(156, 39, 176, 0.3)',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              letterSpacing: '0.5px',
              zIndex: 10
            }}
          >
            <SearchIcon fontSize="small" />
            تصفية البيانات
          </Box>
          
          {/* Active Filters Display */}
          {(filters.search || filters.status) && (
            <Box
              sx={{
                position: 'absolute',
                top: -18,
                left: 24,
                display: 'flex',
                gap: 1,
                flexWrap: 'wrap'
              }}
            >
              {filters.search && (
                <Chip
                  label={`بحث: ${filters.search}`}
                  size="small"
                  onDelete={() => handleFilterChange('search', '')}
                  sx={{
                    bgcolor: 'rgba(33, 150, 243, 0.1)',
                    color: 'info.dark',
                    fontWeight: 'medium',
                    boxShadow: '0 3px 5px rgba(0,0,0,0.05)',
                    '&:hover': { bgcolor: 'rgba(33, 150, 243, 0.15)' }
                  }}
                />
              )}
              {filters.status && (
                <Chip
                  label={`الحالة: ${filters.status === 'active' ? 'نشط' : 'غير نشط'}`}
                  size="small"
                  onDelete={() => handleFilterChange('status', '')}
                  sx={{
                    bgcolor: filters.status === 'active' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                    color: filters.status === 'active' ? 'success.dark' : 'error.dark',
                    fontWeight: 'medium',
                    boxShadow: '0 3px 5px rgba(0,0,0,0.05)',
                    '&:hover': {
                      bgcolor: filters.status === 'active' ? 'rgba(76, 175, 80, 0.15)' : 'rgba(244, 67, 54, 0.15)'
                    }
                  }}
                />
              )}
              {(filters.search || filters.status) && (
                <Chip
                  label="إعادة ضبط"
                  size="small"
                  onClick={() => {
                    setFilters({
                      page: 1,
                      limit: 10
                    });
                  }}
                  sx={{
                    bgcolor: 'rgba(0, 0, 0, 0.08)',
                    color: 'text.secondary',
                    fontWeight: 'medium',
                    boxShadow: '0 3px 5px rgba(0,0,0,0.05)',
                    '&:hover': {
                      bgcolor: 'rgba(0, 0, 0, 0.12)',
                      color: 'text.primary'
                    }
                  }}
                />
              )}
            </Box>
          )}
          
          <CardContent
            sx={{
              p: 3,
              pt: 4,
              background: 'linear-gradient(180deg, rgba(246,249,252,0.4) 0%, rgba(255,255,255,1) 100%)'
            }}
          >
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={6}>
                <Box
                  sx={{
                    mb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <SearchIcon sx={{ color: 'info.main', fontSize: '0.9rem' }} />
                  <Typography variant="caption" fontWeight="medium" color="info.main">
                    بحث سريع
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  placeholder="البحث بالاسم، اسم المستخدم أو البريد..."
                  variant="outlined"
                  size="small"
                  value={filters.search || ''}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  InputProps={{
                    startAdornment: <SearchIcon color="action" sx={{ mr: 1, color: 'text.secondary' }} />,
                    sx: {
                      borderRadius: 3,
                      bgcolor: 'white',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.06)',
                      '&:hover': {
                        boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                      },
                      transition: 'all 0.2s',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.08)',
                        borderWidth: '1px'
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(33, 150, 243, 0.15)'
                      }
                    }
                  }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6} md={6}>
                <Box
                  sx={{
                    mb: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  <Box
                    component="span"
                    sx={{
                      width: 8,
                      height: 8,
                      bgcolor: 'error.main',
                      borderRadius: '50%',
                      display: 'inline-block'
                    }}
                  />
                  <Typography variant="caption" fontWeight="medium" color="error.main">
                    حالة الحساب
                  </Typography>
                </Box>
                <TextField
                  fullWidth
                  select
                  placeholder="اختر الحالة"
                  size="small"
                  value={filters.status || ''}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  InputProps={{
                    sx: {
                      borderRadius: 3,
                      bgcolor: 'white',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.06)',
                      '&:hover': {
                        boxShadow: '0 4px 10px rgba(0,0,0,0.1)'
                      },
                      transition: 'all 0.2s',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: 'rgba(0,0,0,0.08)',
                        borderWidth: '1px'
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 0 0 3px rgba(244, 67, 54, 0.15)'
                      }
                    }
                  }}
                >
                  <MenuItem value="">
                    <Box sx={{ opacity: 0.7, fontStyle: 'italic' }}>جميع الحالات</Box>
                  </MenuItem>
                  <MenuItem value="active">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          width: 8,
                          height: 8,
                          bgcolor: 'success.main',
                          borderRadius: '50%',
                          display: 'inline-block'
                        }}
                      />
                      نشط
                    </Box>
                  </MenuItem>
                  <MenuItem value="inactive">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        component="span"
                        sx={{
                          width: 8,
                          height: 8,
                          bgcolor: 'error.main',
                          borderRadius: '50%',
                          display: 'inline-block'
                        }}
                      />
                      غير نشط
                    </Box>
                  </MenuItem>
                </TextField>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Box>

      {/* Table/Cards Section - Full Width */}
      <Box sx={{ px: 0, pb: 4, pt: 0, width: '100%' }}>
        {isLoading ? (
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 10
          }}>
            <Typography variant="body1" sx={{ color: 'text.secondary', mr: 2 }}>
              جاري تحميل البيانات...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: 10,
            px: 3,
            mx: 3,
            my: 2,
            bgcolor: 'rgba(244, 67, 54, 0.1)',
            border: '1px solid',
            borderColor: 'error.main',
            borderRadius: 2
          }}>
            <Typography variant="h6" sx={{ mb: 2, color: 'error.dark', fontWeight: 'bold' }}>
              خطأ في تحميل البيانات
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, color: 'error.dark', textAlign: 'center', px: 2 }}>
              {error}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => fetchUsers()}
              sx={{
                fontWeight: 'bold',
                boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
              }}
            >
              إعادة المحاولة
            </Button>
          </Box>
        ) : users.length === 0 ? (
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            py: 10,
            color: 'text.secondary'
          }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              لا توجد نتائج مطابقة للبحث
            </Typography>
            <Typography variant="body2">
              حاول تغيير معايير البحث أو إضافة مستخدمين جدد
            </Typography>
          </Box>
        ) : (
          // Always show Table View regardless of screen size
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <TableContainer
              component={Paper}
              sx={{
                mt: 0,
                borderRadius: '12px',
                overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                border: '1px solid rgba(0,0,0,0.04)',
                width: '100%', // Changed to full width
                maxWidth: '100%'
              }}
            >
              <Table stickyHeader aria-label="جدول المستخدمين" sx={{ tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    {[
                      'اسم المستخدم',
                      'الاسم الكامل',
                      'البريد الإلكتروني',
                      'الهاتف',
                      'الحالة',
                      'آخر تسجيل دخول',
                      'الإجراءات'
                    ].map((header, index) => (
                      <TableCell
                        key={index}
                        align="center"
                        sx={{
                          bgcolor: 'secondary.main',
                          color: 'white',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                          borderBottom: '2px solid #9c27b0',
                          py: 2.5,
                          textAlign: 'center',
                          position: 'relative',
                          minWidth: header === 'البريد الإلكتروني' ? 180 : 'auto',
                          width: header === 'الإجراءات' ? 150 : 'auto',
                          '&::after': index < 6 ? {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '70%',
                            width: '1px',
                            backgroundColor: 'rgba(255, 255, 255, 0.3)'
                          } : {},
                          letterSpacing: '0.5px'
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user, index) => (
                    <TableRow
                      key={user._id}
                      sx={{
                        bgcolor: index % 2 === 0 ? 'white' : 'rgba(245, 247, 250, 1)',
                        '&:hover': {
                          bgcolor: 'rgba(156, 39, 176, 0.04)',
                          boxShadow: 'inset 0 0 0 1px rgba(156, 39, 176, 0.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      {/* Username Cell */}
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: '#2c3e50',
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2,
                          px: 2,
                          position: 'relative',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '70%',
                            width: '1px',
                            backgroundColor: 'rgba(0, 0, 0, 0.05)'
                          }
                        }}
                      >
                        <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
                          <PersonIcon color="secondary" fontSize="small" />
                          {user.username}
                        </Stack>
                      </TableCell>
                      
                      {/* Full Name Cell */}
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: '0.95rem',
                          fontWeight: 500,
                          color: '#2c3e50',
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2,
                          px: 2,
                          position: 'relative',
                          whiteSpace: 'nowrap',
                          maxWidth: { xs: '100px', sm: '120px', md: '150px' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '70%',
                            width: '1px',
                            backgroundColor: 'rgba(0, 0, 0, 0.05)'
                          }
                        }}
                      >
                        {user.fullName}
                      </TableCell>
                      
                      {/* Email Cell */}
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: '0.95rem',
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2,
                          px: 2,
                          color: 'info.main',
                          position: 'relative',
                          textAlign: 'center',
                          maxWidth: { xs: '120px', sm: '150px', lg: '200px' },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '70%',
                            width: '1px',
                            backgroundColor: 'rgba(0, 0, 0, 0.05)'
                          }
                        }}
                      >
                        {user.email || '-'}
                      </TableCell>
                      
                      {/* Phone Cell */}
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: '0.95rem',
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2,
                          px: 2,
                          fontFamily: 'monospace', // Better for numbers
                          letterSpacing: '0.5px',
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '70%',
                            width: '1px',
                            backgroundColor: 'rgba(0, 0, 0, 0.05)'
                          }
                        }}
                      >
                        {user.phone || '-'}
                      </TableCell>
                      
                      {/* Status Cell */}
                      <TableCell
                        align="center"
                        sx={{
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2,
                          px: 2,
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '70%',
                            width: '1px',
                            backgroundColor: 'rgba(0, 0, 0, 0.05)'
                          }
                        }}
                      >
                        <Chip
                          label={user.status === 'active' ? 'نشط' : 'غير نشط'}
                          size="small"
                          sx={{
                            borderRadius: 1,
                            fontWeight: 500,
                            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                            '& .MuiChip-label': { px: 1.5 },
                            bgcolor: user.status === 'active' ? 'rgba(76, 175, 80, 0.1)' : 'rgba(244, 67, 54, 0.1)',
                            color: user.status === 'active' ? 'success.dark' : 'error.dark'
                          }}
                        />
                      </TableCell>
                      
                      {/* Last Login Cell */}
                      <TableCell
                        align="center"
                        sx={{
                          fontSize: '0.9rem',
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2,
                          px: 2,
                          position: 'relative',
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            right: 0,
                            top: '50%',
                            transform: 'translateY(-50%)',
                            height: '70%',
                            width: '1px',
                            backgroundColor: 'rgba(0, 0, 0, 0.05)'
                          }
                        }}
                      >
                        {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString('ar-SA') : '-'}
                      </TableCell>
                      
                      {/* Actions Cell */}
                      <TableCell
                        align="center"
                        sx={{
                          borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
                          py: 2,
                          px: 2
                        }}
                      >
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          {hasPermission('admin', 'users', 'view') && (
                            <Tooltip title="عرض التفاصيل" arrow>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/users/${user._id}`)}
                                sx={{
                                  color: 'info.main',
                                  bgcolor: 'rgba(33, 150, 243, 0.1)',
                                  width: 34,
                                  height: 34,
                                  '&:hover': {
                                    bgcolor: 'rgba(33, 150, 243, 0.2)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 3px 8px rgba(33, 150, 243, 0.2)'
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <SearchIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasPermission('admin', 'users', 'edit') && (
                            <Tooltip title="تعديل" arrow>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/users/edit/${user._id}`)}
                                sx={{
                                  color: 'warning.main',
                                  bgcolor: 'rgba(255, 152, 0, 0.1)',
                                  width: 34,
                                  height: 34,
                                  '&:hover': {
                                    bgcolor: 'rgba(255, 152, 0, 0.2)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 3px 8px rgba(255, 152, 0, 0.2)'
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          {hasPermission('admin', 'users', 'delete') && (
                            <Tooltip title="حذف" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(user._id)}
                                sx={{
                                  color: 'error.main',
                                  bgcolor: 'rgba(244, 67, 54, 0.1)',
                                  width: 34,
                                  height: 34,
                                  '&:hover': {
                                    bgcolor: 'rgba(244, 67, 54, 0.2)',
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 3px 8px rgba(244, 67, 54, 0.2)'
                                  },
                                  transition: 'all 0.2s'
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <TablePagination
                component="div"
                count={total}
                page={(filters.page || 1) - 1}
                onPageChange={handlePageChange}
                rowsPerPage={filters.limit || 10}
                rowsPerPageOptions={[10, 25, 50]}
                labelRowsPerPage="عدد النتائج:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
                sx={{
                  bgcolor: 'white',
                  borderTop: '2px solid rgba(224, 224, 224, 0.5)',
                  py: 2,
                  px: 3,
                  '& .MuiTablePagination-toolbar': {
                    borderRadius: 2,
                    p: 1.5,
                    backgroundColor: 'rgba(0, 0, 0, 0.02)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    flexWrap: 'wrap',
                    gap: 1
                  },
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows': {
                    fontWeight: 500,
                    color: '#2c3e50',
                    fontSize: '0.95rem',
                    m: 0
                  },
                  '& .MuiTablePagination-select': {
                    borderRadius: 1.5,
                    mx: 1,
                    border: '1px solid rgba(0, 0, 0, 0.08)',
                    py: 0.5,
                    pl: 1.5,
                    pr: 0.5
                  },
                  '& .MuiIconButton-root': {
                    color: 'primary.main',
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    '&:hover': {
                      bgcolor: 'rgba(25, 118, 210, 0.1)',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 5px rgba(25, 118, 210, 0.15)'
                    },
                    '&.Mui-disabled': {
                      opacity: 0.5,
                      bgcolor: 'transparent',
                      boxShadow: 'none'
                    },
                    transition: 'all 0.2s'
                  },
                  '& .MuiTablePagination-actions': {
                    display: 'flex',
                    gap: 0.5
                  }
                }}
              />
            </TableContainer>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UserList;